import { ArchiveBoxIcon, Bars3Icon, BookOpenIcon, ChartBarIcon, HomeIcon, ListBulletIcon, PhoneIcon, PhotoIcon, QrCodeIcon, VideoCameraIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { BarcodeScanner } from '@/components/Ui/BarcodeScanner';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger
} from '@/components/Ui/Sheet';
import auth from '@/helpers/auth';

const navItems = [
    { id: 'home', label: 'Home', path: '/', icon: HomeIcon },
    { id: 'catalog', label: 'Catalog', path: '/catalog', icon: ArchiveBoxIcon },
    { id: 'quotes', label: 'Quotes', path: '/quotes', icon: ListBulletIcon },
    // { id: 'contact', label: 'Contact', path: '/contact', icon: PhoneIcon },
];

const mediaItems = [
    { label: 'Images', icon: PhotoIcon, to: '/media/images' },
    { label: 'Video', icon: VideoCameraIcon, to: '/media/video' },
    { label: 'Catalogues', icon: BookOpenIcon, to: '/media/catalogues' },
];

export function MobileNav() {
    const [
        t,
    ] = useTranslation('pages');
    const [
        moreOpen,
        setMoreOpen,
    ] = useState(false);
    const [
        scannerOpen,
        setScannerOpen,
    ] = useState(false);
    const isAdministrator = auth.isAdministrator();

    const visibleNavItems = isAdministrator
        ? navItems.filter(({ id }) => {
            return id !== 'quotes';
        })
        : navItems;

    return (
        <>
            <nav className='md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-sm border-t border-border safe-area-pb'>
                <div className='flex items-center justify-around py-2'>
                    {visibleNavItems.map(({ id, label, path, icon: Icon }) => {
                        return (
                            <NavLink
                                className={ ({ isActive }) => {
                                    return clsx({
                                        'flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors': true,
                                        'text-primary': isActive,
                                        'text-muted-foreground hover:text-foreground': !isActive,
                                    });
                                } }
                                key={ id }
                                to={ path }
                            >
                                <Icon className='h-5 w-5' />
                                <span className='text-xs font-medium'>{t(label)}</span>
                            </NavLink>
                        );
                    })}

                    {isAdministrator && (
                        <button
                            className='flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground focus:outline-none'
                            onClick={ () => {
                                return setScannerOpen(true);
                            } }
                            type='button'
                        >
                            <QrCodeIcon className='h-5 w-5' />
                            <span className='text-xs font-medium'>{t('Scan')}</span>
                        </button>
                    )}

                    {/* More Menu */}
                    <Sheet onOpenChange={ setMoreOpen } open={ moreOpen }>
                        <SheetTrigger asChild>
                            <button className='flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground focus:outline-none'>
                                <Bars3Icon className='h-5 w-5' />
                                <span className='text-xs font-medium'>More</span>
                            </button>
                        </SheetTrigger>
                        <SheetContent className='rounded-t-2xl pb-8' side='bottom'>
                            <SheetHeader className='pb-4 border-b border-border mb-4'>
                                <SheetTitle>Menu</SheetTitle>
                            </SheetHeader>

                            <div className='space-y-6'>
                                {/* Quick Links */}
                                <div className='space-y-1'>
                                    <SheetClose asChild>
                                        <NavLink
                                            className='flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground hover:bg-muted transition-colors'
                                            to='/contact'
                                        >
                                            <PhoneIcon className='h-5 w-5 text-muted-foreground' />
                                            <span className='font-medium'>Contact</span>
                                        </NavLink>
                                    </SheetClose>

                                    {isAdministrator && (
                                        <SheetClose asChild>
                                            <NavLink
                                                className='w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground hover:bg-muted transition-colors'
                                                to='/quotes'
                                            >
                                                <ListBulletIcon className='h-5 w-5 text-muted-foreground' />
                                                <span className='font-medium'>Quotes</span>
                                            </NavLink>
                                        </SheetClose>
                                    )}

                                    {isAdministrator && (
                                        <SheetClose asChild>
                                            <NavLink
                                                className='w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground hover:bg-muted transition-colors'
                                                to='/stocks'
                                            >
                                                <ChartBarIcon className='h-5 w-5 text-muted-foreground' />
                                                <span className='font-medium'>Stock Management</span>
                                            </NavLink>
                                        </SheetClose>
                                    )}

                                    {isAdministrator && (
                                        <SheetClose asChild>
                                            <NavLink
                                                className='w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground hover:bg-muted transition-colors'
                                                to='/analysis/stock-valuation'
                                            >
                                                <ChartBarIcon className='h-5 w-5 text-muted-foreground' />
                                                <span className='font-medium'>Analysis</span>
                                            </NavLink>
                                        </SheetClose>
                                    )}

                                </div>

                                {/* Media Section */}
                                <div>
                                    <p className='px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2'>Media</p>
                                    <div className='grid grid-cols-2 gap-2'>
                                        {mediaItems.map(({ label, icon: Icon, to }) => {
                                            return (
                                                <SheetClose asChild key={ to }>
                                                    <NavLink
                                                        className='flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors'
                                                        to={ to }
                                                    >
                                                        <Icon className='h-4 w-4 text-muted-foreground' />
                                                        <span className='text-sm font-medium'>{label}</span>
                                                    </NavLink>
                                                </SheetClose>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </nav>
            {scannerOpen && (
                <BarcodeScanner onClose={ () => {
                    return setScannerOpen(false);
                } } />
            )}
        </>
    );
}
